import { useCallback, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthOptionsQuery, useAuthRegisterMutation } from '@platform/features/auth';
import { AuthField } from './AuthField';
import { AuthFrame } from './AuthFrame';
import { authErrorKey } from './authErrorKey';
import { authRoutes } from './authRoutes';
import { registrationError } from './registrationError';
import { SsoButtons } from './SsoButtons';
import { useEnterApp } from './useEnterApp';
import { useSsoSignIn } from './useSsoSignIn';

/**
 * Реєстрація (прототип v4 screen 'register'): «Створіть обліковий запис», «Далі ви налаштуєте свій перший робочий простір.», повне ім'я,
 * кнопки SSO, e-mail, пароль ≥ 8 символів із текстами перевірки прототипу, «Уже маєте обліковий запис? Увійти». Доступна лише з увімкненою
 * самостійною реєстрацією (signIn.selfRegistration, інакше — вхід); після реєстрації — створення першого простору зі зразковими записами.
 */
export function RegisterPage() {
  const { t } = useTranslation();
  const { data: options, isError } = useAuthOptionsQuery();
  const [register, { isLoading }] = useAuthRegisterMutation();
  const enter = useEnterApp();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const onError = useCallback((code: string) => setError(t(authErrorKey(code))), [t]);
  const sso = useSsoSignIn(options, onError);
  const edit = (set: (v: string) => void) => (v: string) => { set(v); setError(''); };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const invalid = registrationError(name, email, password);
    if (invalid) { onError(invalid); return; }
    register({ name: name.trim(), email: email.trim().toLowerCase(), password }).unwrap()
      .then(() => enter(authRoutes.onboarding(), { sample: true }), (err: { code?: string }) => onError(err.code ?? ''));
  };
  if (options && !options.selfRegistration) return <Navigate to={authRoutes.login()} replace />;
  if (!options) return <AuthFrame><div className="au__form">{isError && <div className="au__error" role="alert">{t('auth.errors.failed')}</div>}</div></AuthFrame>;
  const { providers } = options;
  return (
    <AuthFrame>
      <form className="au__form" onSubmit={submit} noValidate>
        <div className="au__head"><h1>{t('auth.registerTitle')}</h1><div className="au__sub">{t('auth.registerSub')}</div></div>
        <AuthField label={t('auth.fullName')} type="text" value={name} placeholder={t('auth.fullNamePh')} autoComplete="name" onChange={edit(setName)} />
        <SsoButtons providers={providers} onSso={sso} />
        {(providers.google || providers.microsoft) && <div className="au__or">{t('auth.orEmail')}</div>}
        <AuthField label={t('auth.email')} type="email" value={email} placeholder={t('auth.emailPh')} autoComplete="email" onChange={edit(setEmail)} />
        <AuthField label={t('auth.password')} type="password" value={password} placeholder={t('auth.passwordPh')} autoComplete="new-password" onChange={edit(setPassword)} />
        {error && <div className="au__error" role="alert">{error}</div>}
        <button type="submit" className="au__submit" disabled={isLoading}>{t('auth.createAccount')}</button>
        <div className="au__switch"><span>{t('auth.haveAccount')}</span><Link to={authRoutes.login()}>{t('auth.signIn')}</Link></div>
      </form>
    </AuthFrame>
  );
}
